import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Flex,
  Text,
  Heading,
  useOutsideClick,
  Collapse,
  Divider,
  Input,
} from "@chakra-ui/react"
import { FaFilter } from "react-icons/fa"
import { ArrowDownIcon2 } from "@/styles/icons/ArrowDownIcon2"
import { CalendarIcon } from "@/styles/icons"
import { FilterOption } from "@/models"
import { subMonths } from "date-fns";
import { Button } from "barte-design-system"
import { useTreasury } from "@/hooks"

interface RendimentosFilterTableProps {
  onFilterChange: (filterState: any) => void
}

export function RendimentosFilter({ onFilterChange }: RendimentosFilterTableProps) {
    const pad = (n: number) => String(n).padStart(2, "0");
    const toYMD = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = toYMD(new Date());

    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [activeFilters, setActiveFilters] = useState<string[]>(["date"])
    const { filterState } = useTreasury();
    const [localFilterState, setLocalFilterState] = useState<any>({...filterState}) 
    const [hasAppliedFilters, setHasAppliedFilters] = useState(false)
    const filterRef = useRef<HTMLDivElement>(null)
    const clearFilterState = {
        startDate: null,
        endDate: null
    }
    const filterOptions: FilterOption[] = [
        { 
            icon: <CalendarIcon size={12} />,
            id: "date", 
            label: "Data", 
            type: "date" 
        },
    ]

    useOutsideClick({
        ref: filterRef,
        handler: () => setIsFilterOpen(false),
    })

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen)
    }

    const toggleFilterOption = (id: string) => {
        if (activeFilters.includes(id)) {
        setActiveFilters(activeFilters.filter((filterId) => filterId !== id))
        } else {
        setActiveFilters([...activeFilters, id])
        }
    }

    const handleClearFilters = () => {
        setLocalFilterState(clearFilterState);
        setHasAppliedFilters(false);
        setIsFilterOpen(false);
        onFilterChange(clearFilterState);
    };

    const handleFilterChange = (id: string, value: any) => {
        setLocalFilterState(prev => ({
            ...prev,
            [id]: value,
        }))
    }
    const applyFilters = () => {
        const hasFilterChanges = JSON.stringify(localFilterState) !== JSON.stringify(clearFilterState)
        setHasAppliedFilters(hasFilterChanges)
        setIsFilterOpen(false)
        onFilterChange({...localFilterState})
    }

    

    const addMonths = (dateStr: string, months: number) => {
        const [y, m, d] = dateStr.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        date.setMonth(date.getMonth() + months);
        return toYMD(date);
    };

    const getEndDateMax = (startDate?: string | null) => {
        if (!startDate) return today;
        const max3Months = addMonths(startDate, 3);
        return max3Months < today ? max3Months : today;
    };

    return (
        <>
            <Flex>
                <Flex
                    w="100%"
                    flexDir="column"
                    gap="2"
                >
                <Flex
                    align="center"  
                    gap="2"
                >
                    <Box ref={filterRef}>
                    <Button
                        variant="outline"
                        onClick={toggleFilter}
                        size="sm"
                        iconLeft={<FaFilter size="14px" color="currentColor"/>}
                    >
                        Filtro
                    </Button>

                    <AnimatePresence>
                        {isFilterOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, height: 0 }}
                                animate={{ opacity: 1, y: 0, height: 0 }}
                                exit={{ opacity: 0, y: -10, height: 0 }}
                                transition={{ duration: 0.2 }}
                                style={{
                                position: "relative",
                                }}
                            >
                                <Box
                                    position="absolute"
                                    right="0"
                                    mt="2"
                                    w="80"
                                    bg="white"
                                    borderWidth="1px"
                                    borderRadius="md"
                                    boxShadow="lg"
                                    zIndex="20"
                                    overflow="hidden"
                                >
                                <Flex justify="space-between" align="center" p="4" borderBottomWidth="1px">
                                    <Heading as="h3" size="sm" fontWeight="medium">
                                        Filtros
                                    </Heading>
                                    <Button
                                        variant="link" 
                                        size="sm" 
                                        onClick={handleClearFilters}
                                    >
                                        Limpar Todos
                                    </Button>
                                </Flex>

                                <Box p="4" maxH="96" overflowY="auto">
                                    {filterOptions.map((option) => (
                                        <Box key={option.id} mb="4">
                                            <Flex
                                                align="center"
                                                justify="space-between"
                                                cursor="pointer"
                                                mb="2"
                                                onClick={() => toggleFilterOption(option.id)}
                                            >
                                            <Flex align="center">
                                                <Text mr="2">
                                                    {option.icon}
                                                </Text>
                                                <Text>{option.label}</Text>
                                            </Flex>
                                            <Box
                                                transition="transform 0.2s"
                                                transform={
                                                activeFilters.includes(option.id) 
                                                    ? "rotate(180deg)" 
                                                    : "rotate(0deg)"
                                                }
                                            >
                                                <ArrowDownIcon2 />
                                            </Box>
                                            </Flex>

                                            <Collapse in={activeFilters.includes(option.id)}>
                                            {option.type === "date" && (
                                                <Flex mt="2" gap="2">
                                                <Input 
                                                    type="date"
                                                    name="startDate"
                                                    value={localFilterState?.startDate?.toString()}
                                                    onChange={(e) => {
                                                        const value = e.target.value || null
                                                        if (option.id === "date") {
                                                            handleFilterChange("startDate", value)
                                                            const newEndMax = getEndDateMax(value);
                                                            if (localFilterState?.endDate && localFilterState?.endDate > newEndMax) {
                                                                handleFilterChange("endDate", newEndMax);
                                                            }
                                                        }
                                                    }}
                                                    max={today}
                                           
                                                />
                                                <Input 
                                                    type="date"
                                                    name="endDate"
                                                    value={localFilterState?.endDate?.toString()}
                                                    onChange={(e) => {
                                                        const value = e.target.value || null
                                                        if (option.id === "date") {
                                                            handleFilterChange("endDate", value)
                                                        }
                                                    }}
                                             
                                                    max={getEndDateMax(localFilterState?.startDate)}
                                                    min={localFilterState?.startDate}
                                                    isDisabled={!localFilterState?.startDate}
                                                />
                                                </Flex>
                                            )}
                                            </Collapse>
                                        </Box>
                                    ))}
                                </Box>
                                <Divider />
                                <Flex p="4" justify="flex-end">
                                    <Button 
                                        size="sm"
                                        onClick={applyFilters}
                                    >
                                        Aplicar Filtros
                                    </Button>
                                </Flex>
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    </Box>
                </Flex>
                </Flex>
            </Flex>
        </>

    )
    }